import type { StoreDto } from '@cartly/shared';
import { Alert, Badge, Card, EmptyState, Modal, PageHeader, Spinner } from '@cartly/ui';
import { Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api/client';
import type { CustomerDetail, CustomerRow, Page } from '../../api/types';
import { LoadMore, StatusBadge } from '../../components/Common';
import { formatDate, formatMoney, formatRelative, personName } from '../../app/format';
import { useLoader, usePaged } from '../../app/hooks';
import { useActiveSession } from '../../app/session';
import { CustomerCrm } from './CustomerCrm';

function CustomerModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, error, loading } = useLoader(() => api.get<CustomerDetail>(`/customers/${id}`), id);
  return (
    <Modal open title={data ? personName(data) : 'Customer'} onClose={onClose}>
      {loading && !data ? (
        <Spinner />
      ) : error || !data ? (
        <Alert tone="critical">{error ?? 'Failed to load'}</Alert>
      ) : (
        <div className="stack">
          <dl className="dl">
            <dt>Email</dt>
            <dd>{data.email ?? <span className="text-muted">Not provided</span>}</dd>
            <dt>Phone</dt>
            <dd>{data.phone ?? <span className="text-muted">Not provided</span>}</dd>
            <dt>Stores</dt>
            <dd className="row">
              {data.stores.map((s) => (
                <Badge key={s.id}>{s.name}</Badge>
              ))}
            </dd>
            <dt>Customer since</dt>
            <dd>{formatDate(data.createdAt)}</dd>
            <dt>Last order</dt>
            <dd>{data.lastOrderAt ? formatRelative(data.lastOrderAt) : 'Never ordered'}</dd>
            <dt>Spend</dt>
            <dd>
              {data.spendByCurrency.length === 0 ? (
                <span className="text-muted">No orders yet</span>
              ) : (
                data.spendByCurrency.map((s) => (
                  <div key={s.currency}>
                    {formatMoney(s.total, s.currency)}{' '}
                    <span className="text-muted text-sm">
                      · {s.orders} {s.orders === 1 ? 'order' : 'orders'}
                      {Number(s.refunded) > 0 &&
                        ` · ${formatMoney(s.refunded, s.currency)} refunded`}
                    </span>
                  </div>
                ))
              )}
            </dd>
          </dl>

          <CustomerCrm customerId={id} />

          <h3 className="section-title">Recent orders</h3>
          {data.orders.length === 0 ? (
            <p className="text-muted">No orders yet.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Status</th>
                    <th>Placed</th>
                    <th className="table__actions">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((o) => (
                    <tr key={o.id}>
                      <td>
                        <div className="table__primary">{o.orderNumber ?? o.externalId}</div>
                        <div className="table__secondary">{o.store.name}</div>
                      </td>
                      <td>
                        <StatusBadge
                          status={o.cancelledAt ? 'voided' : o.financialStatus}
                          label={o.cancelledAt ? 'cancelled' : undefined}
                        />
                      </td>
                      <td className="text-muted text-sm">{formatDate(o.placedAt)}</td>
                      <td className="table__actions">{formatMoney(o.total, o.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.checkouts.length > 0 && (
            <>
              <h3 className="section-title">Carts &amp; checkouts</h3>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Items</th>
                      <th>Last activity</th>
                      <th className="table__actions">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.checkouts.map((k) => (
                      <tr key={k.id}>
                        <td className="text-sm">
                          {k.kind === 'cart' ? 'Cart' : 'Checkout'}
                          <div className="table__secondary">{k.store.name}</div>
                        </td>
                        <td>
                          <StatusBadge status={k.status} />
                        </td>
                        <td>{k.itemCount}</td>
                        <td className="text-muted text-sm">{formatRelative(k.lastActivityAt)}</td>
                        <td className="table__actions">{formatMoney(k.total, k.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function CustomersPage() {
  const session = useActiveSession();
  const [text, setText] = useState('');
  const [storeId, setStoreId] = useState('');
  const [buyers, setBuyers] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const q = useDebounced(text.trim(), 300);

  const stores = useLoader(
    () => api.get<{ items: StoreDto[] }>('/stores').then((r) => r.items),
    session.activeTenant?.id,
  );
  const query = new URLSearchParams({
    limit: '50',
    ...(q && { q }),
    ...(storeId && { storeId }),
    ...(buyers && { buyers }),
  });
  const list = usePaged(
    (cursor) =>
      api.get<Page<CustomerRow>>(`/customers?${query}${cursor ? `&cursor=${cursor}` : ''}`),
    `${session.activeTenant?.id}:${q}:${storeId}:${buyers}`,
  );
  const filtered = q !== '' || storeId !== '' || buyers !== '';

  return (
    <>
      <PageHeader
        title="Customers"
        description="People who have shopped or browsed in your connected stores, merged by email and phone."
        actions={<Link to="/customers/segments">Segments</Link>}
      />
      <Card
        flush
        title="Customers"
        actions={
          <div className="row">
            <input
              className="input input--inline"
              type="search"
              aria-label="Search customers"
              placeholder="Search name, email or phone"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <select
              className="select select--inline"
              aria-label="Store"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
            >
              <option value="">All stores</option>
              {(stores.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <select
              className="select select--inline"
              aria-label="Orders"
              value={buyers}
              onChange={(e) => setBuyers(e.target.value)}
            >
              <option value="">Everyone</option>
              <option value="yes">Has ordered</option>
              <option value="no">Never ordered</option>
            </select>
          </div>
        }
      >
        {list.loading && list.items.length === 0 ? (
          <div className="card__body">
            <Spinner />
          </div>
        ) : list.error ? (
          <div className="card__body">
            <Alert tone="critical">{list.error}</Alert>
          </div>
        ) : list.items.length === 0 ? (
          <EmptyState
            icon={<Users size={22} />}
            title={filtered ? 'No customers match' : 'No customers yet'}
          >
            {filtered
              ? 'Try a different search or clear the filters.'
              : 'Customers appear as your stores sync orders, carts and customer records.'}
          </EmptyState>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Phone</th>
                    <th>Stores</th>
                    <th>Orders</th>
                    <th>Last order</th>
                    <th className="table__actions">Total spent</th>
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => setOpen(c.id)}
                      tabIndex={0}
                      onKeyDown={(k) => k.key === 'Enter' && setOpen(c.id)}
                    >
                      <td>
                        <div className="table__primary">{personName(c)}</div>
                        {c.tags.length > 0 && (
                          <div className="row">
                            {c.tags.map((t) => (
                              <Badge key={t}>{t}</Badge>
                            ))}
                          </div>
                        )}
                        {c.email && <div className="table__secondary">{c.email}</div>}
                      </td>
                      <td className="text-sm mono">{c.phone ?? '—'}</td>
                      <td className="text-sm">{c.stores.map((s) => s.name).join(', ') || '—'}</td>
                      <td>{c.ordersCount}</td>
                      <td className="text-muted text-sm">
                        {c.lastOrderAt ? formatRelative(c.lastOrderAt) : 'Never'}
                      </td>
                      <td className="table__actions">
                        {c.ordersCount > 0 && c.currency
                          ? formatMoney(c.totalSpent, c.currency)
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.hasMore && (
              <LoadMore onClick={() => void list.loadMore()} loading={list.loadingMore} />
            )}
          </>
        )}
      </Card>
      {open && <CustomerModal id={open} onClose={() => setOpen(null)} />}
    </>
  );
}
